import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { usePlatformSettings, resolveMediaUrl } from '@/lib/platformSettings';
import NotificationBell from '@/components/marketplace/NotificationBell';
import { Menu, Search, LogIn, LogOut, LayoutDashboard, PlusCircle, ShieldCheck, Settings, User as UserIcon, Factory } from 'lucide-react';

const navItems = [
  { to: '/sell', label: 'Sell' },
  { to: '/wanted', label: 'Wanted' },
  { to: '/rent', label: 'Rent' },
  { to: '/services', label: 'Services' },
  { to: '/categories', label: 'Categories' },
  { to: '/locations', label: 'Locations' },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Blog' },
];

export const Header = () => {
  const { user, logout, hasAnyPermission } = useAuth();
  const { brand } = usePlatformSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const initials = user?.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || 'IF';

  // Prefer admin-uploaded brand assets; fall back to the historic hard-coded values
  const brandName = brand?.brand_short_name || 'IndusFact';
  const brandLong = brand?.brand_name || 'IndusFact B2B Solutions';
  const brandSuffix = brandLong.replace(brandName, '').trim() || 'B2B Solutions';
  const logoSrc = brand?.logo_url ? resolveMediaUrl(brand.logo_url) : '/indusfact-logo.png';

  return (
    <header
      data-testid='site-header'
      className='sticky top-0 z-40 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70'
    >
      <div className='indus-container flex h-16 items-center gap-4'>
        <Link
          to='/'
          className='flex items-center gap-2'
          data-testid='site-logo'
          onClick={() => {
            // Always reset scroll on logo click, even when already on '/'
            requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
          }}
        >
          <span className='inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border overflow-hidden shrink-0'>
            <img
              src={logoSrc}
              alt={brandName}
              className='h-8 w-8 object-contain'
            />
          </span>
          <span className='font-semibold text-lg tracking-tight'>
            {brandName}
          </span>
        </Link>

        <nav className='hidden lg:flex items-center gap-1 ml-6'>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium hover:bg-secondary ${
                  isActive ? 'bg-secondary text-foreground' : 'text-muted-foreground'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className='ml-auto flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            className='hidden md:inline-flex'
            onClick={() => navigate('/sell')}
            data-testid='header-search-button'
          >
            <Search className='h-4 w-4 mr-1.5' /> Browse
          </Button>

          {user ? (
            <>
              <NotificationBell />
              <Button
                onClick={() => navigate('/post')}
                size='sm'
                data-testid='header-post-listing-button'
                className='bg-accent text-accent-foreground hover:bg-accent/90'
              >
                <PlusCircle className='h-4 w-4 mr-1.5' /> Post Listing
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm' data-testid='header-user-menu' className='gap-2'>
                    <span className='inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-semibold'>{initials}</span>
                    <span className='hidden sm:inline text-sm'>{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-56'>
                  <DropdownMenuLabel>
                    <div className='flex flex-col'>
                      <span className='font-semibold text-sm'>{user.name}</span>
                      <span className='text-xs text-muted-foreground'>{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid='menu-dashboard'>
                    <LayoutDashboard className='h-4 w-4 mr-2' /> Dashboard
                  </DropdownMenuItem>
                  {hasAnyPermission(['moderator.access']) && (
                    <DropdownMenuItem onClick={() => navigate('/moderator')} data-testid='menu-moderator'>
                      <ShieldCheck className='h-4 w-4 mr-2' /> Moderator Panel
                    </DropdownMenuItem>
                  )}
                  {hasAnyPermission(['admin.access']) && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid='menu-admin'>
                      <Settings className='h-4 w-4 mr-2' /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                    <UserIcon className='h-4 w-4 mr-2' /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid='menu-logout'>
                    <LogOut className='h-4 w-4 mr-2' /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => navigate('/login', { state: { from: location.pathname } })}
                data-testid='header-login-button'
              >
                <LogIn className='h-4 w-4 mr-1.5' /> Login
              </Button>
              <Button
                size='sm'
                onClick={() => navigate('/register')}
                data-testid='header-register-button'
              >
                Register
              </Button>
              <Button
                size='sm'
                variant='outline'
                className='hidden sm:inline-flex border-accent text-accent hover:bg-accent hover:text-accent-foreground'
                onClick={() => navigate('/login', { state: { from: '/post' } })}
                data-testid='header-post-listing-cta'
              >
                <PlusCircle className='h-4 w-4 mr-1.5' /> Post Listing
              </Button>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className='lg:hidden'>
              <Button variant='ghost' size='icon' data-testid='header-mobile-menu-button'>
                <Menu className='h-5 w-5' />
              </Button>
            </SheetTrigger>
            <SheetContent side='right' className='w-72'>
              <div className='mt-6 space-y-1'>
                {navItems.map(n => (
                  <NavLink
                    key={n.to} to={n.to}
                    onClick={() => setOpen(false)}
                    className='block px-3 py-2 rounded-md hover:bg-secondary'
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className='border-t my-2' />
                <NavLink to='/about' onClick={() => setOpen(false)} className='block px-3 py-2 rounded-md hover:bg-secondary'>About</NavLink>
                <NavLink to='/contact' onClick={() => setOpen(false)} className='block px-3 py-2 rounded-md hover:bg-secondary'>Contact</NavLink>
                <NavLink to='/faq' onClick={() => setOpen(false)} className='block px-3 py-2 rounded-md hover:bg-secondary'>FAQ</NavLink>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
